import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { useCallback, useMemo } from 'react'
import type {
  AddMockOrderInput,
  AddMockPrescriptionInput,
  AncFormTemplate,
  AncRegisterRow,
  AncVisit,
  BookPregnancyInput,
  EndPregnancyOutcome,
  MockAncEncounter,
  MockAncInvoiceLine,
  MockAncOrder,
  MockAncPrescription,
  MockOrderStatus,
  MockRxStatus,
  PostnatalVisit,
  PregnancyEpisode,
  PregnancyRiskLevel,
  RecordDeliveryInput,
  UpsertAncVisitInput,
  UpsertPostnatalVisitInput
} from '@type/antenatal.type'
import { PREGNANCY_CLOSED_STATUSES, PREGNANCY_CONTINUUM_STATUSES } from '@type/antenatal.type'
import {
  bookPregnancyEpisode,
  completePregnancyEpisode,
  createAncFormTemplate,
  fetchAncFormTemplates,
  fetchAncVisit,
  fetchAncVisits,
  fetchPostnatalVisits,
  fetchPregnancyEpisode,
  fetchPregnancyEpisodes,
  patchAncVisit,
  patchPostnatalVisit,
  recordDelivery as recordDeliveryApi,
  startAncVisit,
  startLabour as startLabourApi,
  updateAncFormTemplate,
  updatePregnancyEpisode
} from '../api/antenatal'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { ANC_API_RESOURCES } from '../pages/Antatal/api/ancApiContract'
import { DEFAULT_PN_TEMPLATE } from '../pages/Antatal/utils/defaultPnTemplate'
import { gestationalAgeAt } from '../pages/Antatal/utils/gestation'
import { getError, showToast } from '@util'

/** Live ANC APIs (access-emr-core). Mock store removed. */
export const USE_ANC_MOCK = false

export type AncRegisterFilter = 'today' | 'upcoming' | 'high_risk' | 'all'

async function updateEncounterStatus(encounterId: string, status: 'in_consultation' | 'completed') {
  await AXIOS_INSTANCE.patch(`encounters/${encounterId}/update_status`, { status })
}

const END_PREGNANCY_OUTCOME_MAP: Record<
  EndPregnancyOutcome,
  { status: 'transferred' | 'loss' | 'archived'; prefix: string }
> = {
  transferred: { status: 'transferred', prefix: '[outcome:transferred]' },
  loss: { status: 'loss', prefix: '[outcome:loss]' },
  lost_to_follow_up: { status: 'archived', prefix: '[outcome:lost_to_follow_up]' }
}

function buildRegisterRows(
  pregnancies: PregnancyEpisode[],
  visitsByPregnancy: Record<string, AncVisit[]>,
  filter: AncRegisterFilter
): AncRegisterRow[] {
  const today = new Date().toISOString().slice(0, 10)
  const rows: AncRegisterRow[] = []

  for (const preg of pregnancies) {
    if (preg.attributes.status !== 'active') continue
    const visits = (visitsByPregnancy[preg.id] || [])
      .slice()
      .sort((a, b) => a.attributes.scheduled_date.localeCompare(b.attributes.scheduled_date))
    const next =
      visits.find(v => v.attributes.status === 'scheduled' || v.attributes.status === 'in_progress') ||
      visits[visits.length - 1]
    if (!next) continue

    const ga = gestationalAgeAt(preg.attributes.lmp)
    const row: AncRegisterRow = {
      pregnancy_id: preg.id,
      visit_id: next.id,
      patient_id: preg.attributes.patient_id,
      patient_name: preg.attributes.patient_name,
      mrn: preg.attributes.mrn,
      anc_number: preg.attributes.anc_number,
      gestational_age_label: `${ga.weeks}w ${ga.days}d`,
      risk_level: preg.attributes.risk_level,
      next_contact: next.attributes.contact_label,
      scheduled_date: next.attributes.scheduled_date,
      visit_status: next.attributes.status,
      schedule_preset: preg.attributes.schedule_preset,
      order_count: 0,
      rx_count: 0
    }

    if (filter === 'today' && row.scheduled_date !== today) continue
    if (filter === 'upcoming' && row.scheduled_date < today) continue
    if (filter === 'high_risk' && row.risk_level !== 'high') continue
    rows.push(row)
  }

  return rows.sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))
}

export function useAntatal(options?: {
  patientId?: string
  pregnancyId?: string
  visitId?: string
  registerFilter?: AncRegisterFilter
  /** Labour or postpartum encounter, when it is not the prenatal visit encounter. */
  orderEncounterId?: string
}) {
  const queryClient = useQueryClient()
  const invalidateAnc = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['anc'] })
  }, [queryClient])

  const { data: templates = [] } = useQuery({
    queryKey: ['anc', 'templates'],
    queryFn: () => fetchAncFormTemplates()
  })

  const { data: pregnancies = [] } = useQuery({
    queryKey: ['anc', 'pregnancies', options?.patientId],
    queryFn: () =>
      fetchPregnancyEpisodes({
        ...(options?.patientId ? { patient_id: options.patientId } : {}),
        // Patient chart: any open continuum phase. Register still filters client-side to active ANC.
        ...(options?.patientId ? { status: PREGNANCY_CONTINUUM_STATUSES.join(',') } : { status: 'active' })
      })
  })

  const { data: pastPregnancies = [] } = useQuery({
    queryKey: ['anc', 'past_pregnancies', options?.patientId],
    enabled: Boolean(options?.patientId),
    queryFn: () =>
      fetchPregnancyEpisodes({
        patient_id: options!.patientId!,
        status: PREGNANCY_CLOSED_STATUSES.join(',')
      })
  })

  const { data: selectedPregnancy } = useQuery({
    queryKey: ['anc', 'pregnancy', options?.pregnancyId],
    enabled: Boolean(options?.pregnancyId),
    queryFn: () => fetchPregnancyEpisode(options!.pregnancyId!)
  })

  const pregnancy = useMemo(() => {
    if (options?.pregnancyId) {
      return (
        selectedPregnancy ||
        pregnancies.find(p => p.id === options.pregnancyId) ||
        pastPregnancies.find(p => p.id === options.pregnancyId)
      )
    }
    if (options?.patientId) {
      return pregnancies.find(
        p => p.attributes.patient_id === options.patientId && PREGNANCY_CONTINUUM_STATUSES.includes(p.attributes.status)
      )
    }
    return undefined
  }, [options?.pregnancyId, options?.patientId, pregnancies, pastPregnancies, selectedPregnancy])

  const pregnancyIdForVisits = pregnancy?.id || options?.pregnancyId

  const { data: visits = [] } = useQuery({
    queryKey: ['anc', 'visits', pregnancyIdForVisits, options?.patientId],
    enabled: Boolean(pregnancyIdForVisits || options?.patientId),
    queryFn: () =>
      fetchAncVisits({
        ...(pregnancyIdForVisits ? { pregnancy_episode_id: pregnancyIdForVisits } : {}),
        ...(options?.patientId && !pregnancyIdForVisits ? { patient_id: options.patientId } : {})
      })
  })

  const { data: postnatalVisits = [] } = useQuery({
    queryKey: ['anc', 'postnatal_visits', pregnancyIdForVisits],
    enabled: Boolean(pregnancyIdForVisits),
    queryFn: () =>
      fetchPostnatalVisits({
        pregnancy_episode_id: pregnancyIdForVisits!
      })
  })

  const { data: visit } = useQuery({
    queryKey: ['anc', 'visit', options?.visitId],
    enabled: Boolean(options?.visitId),
    queryFn: () => fetchAncVisit(options!.visitId!)
  })

  const encounterId = options?.orderEncounterId || visit?.attributes.encounter_id || undefined
  const orderPatientId = visit?.attributes.patient_id || pregnancy?.attributes.patient_id || options?.patientId

  const { data: labOrdersRes } = useQuery({
    queryKey: ['anc', 'lab_orders', encounterId],
    enabled: Boolean(encounterId),
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get('/lab_orders', {
        params: { encounter_id: encounterId, 'page[size]': 50 }
      })
      return data
    }
  })

  const { data: imagingOrdersRes } = useQuery({
    queryKey: ['anc', 'imaging_orders', encounterId],
    enabled: Boolean(encounterId),
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get('/imaging_orders', {
        params: { encounter_id: encounterId, 'page[size]': 50 }
      })
      return data
    }
  })

  const { data: medicationsRes } = useQuery({
    queryKey: ['anc', 'medications', encounterId],
    enabled: Boolean(encounterId),
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get('/medications', {
        params: { encounter_id: encounterId, 'page[size]': 50 }
      })
      return data
    }
  })

  const { data: invoiceItemsRes } = useQuery({
    queryKey: ['anc', 'invoice', encounterId],
    enabled: Boolean(encounterId),
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get(`/encounters/${encounterId}/invoice_items`)
      return data
    }
  })

  const encounter: MockAncEncounter | undefined = useMemo(() => {
    if (!encounterId || !visit) return undefined
    return {
      id: encounterId,
      type: 'mock_anc_encounter',
      attributes: {
        visit_id: visit.id,
        pregnancy_id: visit.attributes.pregnancy_id,
        patient_id: visit.attributes.patient_id,
        encounter_type: 'outpatient',
        purpose: visit.attributes.contact_label,
        status: 'in_progress',
        started_at: visit.attributes.updated_at,
        completed_at: null
      }
    }
  }, [encounterId, visit])

  const invoiceLines: MockAncInvoiceLine[] = useMemo(() => {
    const rows = Array.isArray(invoiceItemsRes?.data) ? invoiceItemsRes.data : []
    return rows.map((item: any) => {
      const unit = Number(item.attributes?.unit_price) || 0
      const qty = Number(item.attributes?.quantity) || 1
      return {
        id: String(item.id),
        type: 'mock_anc_invoice_line' as const,
        attributes: {
          visit_id: visit?.id || '',
          encounter_id: encounterId || '',
          description: item.attributes?.name || 'Charge',
          category: (item.attributes?.category || 'consultation') as MockAncInvoiceLine['attributes']['category'],
          source: 'consultation' as const,
          unit_price: unit,
          quantity: qty,
          amount: unit * qty
        }
      }
    })
  }, [invoiceItemsRes, visit?.id, encounterId])

  const orders: MockAncOrder[] = useMemo(() => {
    const invoiceRows = Array.isArray(invoiceItemsRes?.data) ? invoiceItemsRes.data : []

    const chargeForNames = (names: string[]) =>
      invoiceRows.reduce((sum: number, item: any) => {
        const desc = String(item.attributes?.name || '').toLowerCase()
        const matches = names.some(t => desc.includes(String(t).toLowerCase()))
        if (!matches) return sum
        const unit = Number(item.attributes?.unit_price) || 0
        const qty = Number(item.attributes?.quantity) || 1
        return sum + unit * qty
      }, 0)

    const labRows = Array.isArray(labOrdersRes?.data) ? labOrdersRes.data : []
    const labOrders: MockAncOrder[] = labRows.map((o: any) => {
      const testNames: string[] = Array.isArray(o.attributes?.test_names) ? o.attributes.test_names : []
      const name = testNames.length ? testNames.join(', ') : o.attributes?.note || o.attributes?.name || 'Lab order'

      return {
        id: String(o.id),
        type: 'mock_anc_order' as const,
        attributes: {
          visit_id: visit?.id || '',
          encounter_id: encounterId || '',
          kind: 'lab' as const,
          name,
          test_names: testNames,
          status: String(o.attributes?.status ?? o.meta?.status ?? 'pending') as MockOrderStatus,
          ordered_at: o.attributes?.ordered_at || o.attributes?.created_at || '',
          resulted_at: o.attributes?.completed_at || null,
          result_summary: undefined,
          charge_amount: chargeForNames(testNames)
        }
      }
    })

    const imagingRows = Array.isArray(imagingOrdersRes?.data) ? imagingOrdersRes.data : []
    const imagingOrders: MockAncOrder[] = imagingRows.map((o: any) => {
      const studyNames: string[] = Array.isArray(o.attributes?.study_names)
        ? o.attributes.study_names
        : Array.isArray(o.attributes?.test_names)
          ? o.attributes.test_names
          : []
      const name = studyNames.length
        ? studyNames.join(', ')
        : o.attributes?.note || o.attributes?.name || 'Imaging order'

      return {
        id: String(o.id),
        type: 'mock_anc_order' as const,
        attributes: {
          visit_id: visit?.id || '',
          encounter_id: encounterId || '',
          kind: 'imaging' as const,
          name,
          test_names: studyNames,
          status: String(o.attributes?.status ?? o.meta?.status ?? 'pending') as MockOrderStatus,
          ordered_at: o.attributes?.ordered_at || o.attributes?.created_at || '',
          resulted_at: o.attributes?.completed_at || null,
          result_summary: undefined,
          charge_amount: chargeForNames(studyNames)
        }
      }
    })

    return [...labOrders, ...imagingOrders]
  }, [labOrdersRes, imagingOrdersRes, invoiceItemsRes, visit?.id, encounterId])

  const prescriptions: MockAncPrescription[] = useMemo(() => {
    const rows = Array.isArray(medicationsRes?.data) ? medicationsRes.data : []
    return rows.map((m: any) => ({
      id: String(m.id),
      type: 'mock_anc_prescription' as const,
      attributes: {
        visit_id: visit?.id || '',
        encounter_id: encounterId || '',
        drug_name: m.attributes?.nf_drug_name || m.attributes?.drug_name || 'Medication',
        dose: m.attributes?.dose_quantity || '—',
        frequency: m.attributes?.notes || m.attributes?.frequency || '—',
        duration: m.attributes?.strength || m.attributes?.duration || '—',
        status: String(m.meta?.status ?? m.attributes?.status ?? 'pending') as MockRxStatus,
        prescribed_at: m.attributes?.start_at || m.attributes?.created_at || '',
        charge_amount: 0
      }
    }))
  }, [medicationsRes, visit?.id, encounterId])

  const invoiceTotal = useMemo(
    () => invoiceLines.reduce((sum, line) => sum + line.attributes.amount, 0),
    [invoiceLines]
  )

  const { data: allVisitsForRegister = [] } = useQuery({
    queryKey: ['anc', 'visits', 'register'],
    queryFn: () => fetchAncVisits({}),
    enabled: !options?.patientId && !options?.visitId
  })

  const registerListEnabled = !options?.patientId && !options?.visitId

  const { data: labourEpisodes = [] } = useQuery({
    queryKey: ['anc', 'pregnancies', 'labour_register'],
    enabled: registerListEnabled,
    queryFn: () => fetchPregnancyEpisodes({ status: 'in_labour,delivered,postnatal' })
  })

  const { data: postpartumRegister = [] } = useQuery({
    queryKey: ['anc', 'postnatal_visits', 'register'],
    enabled: registerListEnabled,
    queryFn: () => fetchPostnatalVisits()
  })

  const registerRows: AncRegisterRow[] = useMemo(() => {
    const visitsByPregnancy: Record<string, AncVisit[]> = {}
    const sourceVisits = options?.patientId ? visits : allVisitsForRegister
    for (const v of sourceVisits) {
      const pid = v.attributes.pregnancy_id
      if (!visitsByPregnancy[pid]) visitsByPregnancy[pid] = []
      visitsByPregnancy[pid].push(v)
    }
    const pregs = options?.patientId && pregnancy ? [pregnancy] : pregnancies
    return buildRegisterRows(pregs, visitsByPregnancy, options?.registerFilter ?? 'all')
  }, [pregnancies, pregnancy, visits, allVisitsForRegister, options?.registerFilter, options?.patientId])

  const gestationalAge = useMemo(() => {
    if (!pregnancy) return undefined
    return gestationalAgeAt(pregnancy.attributes.lmp)
  }, [pregnancy])

  const formTemplate: AncFormTemplate | undefined = useMemo(() => {
    const tmplId = pregnancy?.relationships.form_template.data?.id
    if (tmplId) return templates.find(t => t.id === tmplId)
    return templates.find(t => t.attributes.is_default) || templates[0]
  }, [pregnancy, templates])

  const nextAncNumber = useMemo(() => {
    const year = new Date().getFullYear()
    const seq = pregnancies.length + 1
    return `ANC/${year}/${String(seq).padStart(4, '0')}`
  }, [pregnancies.length])

  const bookPregnancyMut = useMutation({
    mutationFn: bookPregnancyEpisode,
    onSuccess: invalidateAnc
  })
  const saveVisitMut = useMutation({
    mutationFn: ({ visitId, input }: { visitId: string; input: UpsertAncVisitInput }) => patchAncVisit(visitId, input),
    onSuccess: invalidateAnc
  })
  const startVisitMut = useMutation({
    mutationFn: startAncVisit,
    onSuccess: invalidateAnc
  })
  const updateTemplateMut = useMutation({
    mutationFn: updateAncFormTemplate,
    onSuccess: invalidateAnc
  })
  const createTemplateMut = useMutation({
    mutationFn: (name: string) => createAncFormTemplate(name),
    onSuccess: invalidateAnc
  })
  const updatePregnancyMut = useMutation({
    mutationFn: ({ id, attrs }: { id: string; attrs: Parameters<typeof updatePregnancyEpisode>[1] }) =>
      updatePregnancyEpisode(id, attrs),
    onSuccess: invalidateAnc
  })

  const bookPregnancy = useCallback(
    async (input: BookPregnancyInput) => {
      try {
        return await bookPregnancyMut.mutateAsync(input)
      } catch (e) {
        getError(e)
        throw e
      }
    },
    [bookPregnancyMut]
  )

  const saveVisit = useCallback(
    async (visitId: string, input: UpsertAncVisitInput) => {
      try {
        const updated = await saveVisitMut.mutateAsync({ visitId, input })

        if (input.status === 'completed') {
          const encId = input.encounter_id || updated.attributes.encounter_id
          if (encId) {
            try {
              // Prefer completed directly (already in_consultation after Open visit).
              // If still pending, recover: in_consultation → completed.
              try {
                await updateEncounterStatus(encId, 'completed')
              } catch {
                await updateEncounterStatus(encId, 'in_consultation')
                await updateEncounterStatus(encId, 'completed')
              }
              queryClient.invalidateQueries({ queryKey: ['encounters'] })
              queryClient.invalidateQueries({ queryKey: ['intake'] })
              queryClient.invalidateQueries({ queryKey: ['dashboard'] })
            } catch (e) {
              getError(e)
              return undefined
            }
          }
        }

        return updated
      } catch (e) {
        getError(e)
        return undefined
      }
    },
    [saveVisitMut, queryClient]
  )

  const startVisit = useCallback(
    async (visitId: string) => {
      try {
        const started = await startVisitMut.mutateAsync(visitId)
        const encId = started.attributes.encounter_id
        if (encId) {
          try {
            await updateEncounterStatus(encId, 'in_consultation')
            queryClient.invalidateQueries({ queryKey: ['encounters'] })
            queryClient.invalidateQueries({ queryKey: ['intake'] })
          } catch {
            // Already in consultation or invalid — visit can still proceed
          }
        }
        return started
      } catch (e) {
        getError(e)
        return undefined
      }
    },
    [startVisitMut.mutateAsync, queryClient]
  )

  const addOrder = useCallback(
    async (input: AddMockOrderInput) => {
      const enc = input.encounter_id || encounterId
      const patient = input.patient_id || orderPatientId
      if (!enc || !patient) {
        showToast('Start the visit first so an encounter exists')
        return undefined
      }
      if (input.kind === 'procedure') {
        showToast('Use Procedure from Manage Orders on the encounter for now')
        return undefined
      }

      const catalogIds = input.items
        .map(i => Number((i as { catalog_id?: number | string }).catalog_id))
        .filter(n => Number.isFinite(n) && n > 0)
      const names = input.items.map(i => i.name).filter(Boolean)

      try {
        if (input.kind === 'imaging') {
          await AXIOS_INSTANCE.post(
            '/imaging_orders',
            {
              data: {
                type: 'imaging_order',
                attributes: {
                  imaging_ids: catalogIds,
                  ...(names.length ? { study_names: names } : {})
                },
                relationships: {
                  patient: { data: { id: patient, type: 'patient' } },
                  encounter: { data: { id: enc, type: 'encounter' } }
                }
              }
            },
            { headers: { 'Content-Type': 'application/vnd.api+json' } }
          )
          showToast('Imaging order placed')
          queryClient.invalidateQueries({ queryKey: ['anc', 'imaging_orders', enc] })
        } else {
          await AXIOS_INSTANCE.post(
            '/lab_orders',
            {
              data: {
                type: 'lab_order',
                attributes: {
                  lab_test_ids: catalogIds,
                  ...(names.length ? { test_names: names } : {})
                },
                relationships: {
                  patient: { data: { id: patient, type: 'patient' } },
                  encounter: { data: { id: enc, type: 'encounter' } }
                }
              }
            },
            { headers: { 'Content-Type': 'application/vnd.api+json' } }
          )
          showToast('Lab order placed')
          queryClient.invalidateQueries({ queryKey: ['anc', 'lab_orders', enc] })
        }
        invalidateAnc()
        queryClient.invalidateQueries({ queryKey: ['anc', 'invoice', enc] })
      } catch (e) {
        getError(e)
      }
      return undefined
    },
    [encounterId, orderPatientId, invalidateAnc, queryClient]
  )

  const addPrescription = useCallback(
    async (input: AddMockPrescriptionInput) => {
      const enc = input.encounter_id || encounterId
      const patient = input.patient_id || orderPatientId
      if (!enc || !patient) {
        showToast('Start the visit first so an encounter exists')
        return undefined
      }
      try {
        const startAt = input.start_at
          ? moment(input.start_at, ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DD', 'YYYY-MM-DD h:mm a', moment.ISO_8601]).format(
              'YYYY-MM-DD h:mm a'
            )
          : moment().format('YYYY-MM-DD h:mm a')
        const endAt = moment(startAt, 'YYYY-MM-DD h:mm a').add(30, 'days').format('YYYY-MM-DD h:mm a')

        const doseUnit = input.dose_unit?.trim()
        if (!doseUnit) {
          showToast('Select a dose unit')
          return undefined
        }
        const form = input.form?.trim()
        if (!form) {
          showToast('Select a dosage form')
          return undefined
        }

        await AXIOS_INSTANCE.post(
          '/medications',
          {
            data: {
              type: 'medication',
              attributes: {
                nf_drug_name: input.drug_name,
                dose_quantity: input.dose,
                dosage: `${input.dose} ${doseUnit}`,
                strength: input.strength || input.dose,
                dose_unit: doseUnit,
                form,
                route: 'Oral',
                medication_type: 'Routine',
                context: 'outpatient_administered',
                prescribed: true,
                ordered: true,
                start_at: startAt,
                end_at: endAt,
                notes: `${input.frequency} · ${input.duration}`,
                custom_frequency: {}
              },
              relationships: {
                patient: { data: { id: patient, type: 'patient' } },
                encounter: { data: { id: enc, type: 'encounter' } },
                ...(input.frequency_id
                  ? {
                      medication_frequency: {
                        data: { id: input.frequency_id, type: 'medication_frequency' }
                      }
                    }
                  : {})
              }
            }
          },
          { headers: { 'Content-Type': 'application/vnd.api+json' } }
        )
        showToast('Prescription added')
        queryClient.invalidateQueries({ queryKey: ['anc', 'medications', enc] })
      } catch (e) {
        getError(e)
      }
      return undefined
    },
    [encounterId, orderPatientId, queryClient]
  )

  const updateTemplate = useCallback(
    async (tmpl: AncFormTemplate) => {
      try {
        return await updateTemplateMut.mutateAsync(tmpl)
      } catch (e) {
        getError(e)
        return tmpl
      }
    },
    [updateTemplateMut]
  )

  const createTemplate = useCallback(
    async (name: string) => {
      try {
        return await createTemplateMut.mutateAsync(name)
      } catch (e) {
        getError(e)
        throw e
      }
    },
    [createTemplateMut]
  )

  const updatePregnancyRisk = useCallback(
    async (pregnancyId: string, risk_level: PregnancyRiskLevel, risk_flags: string[]) => {
      try {
        const updated = await updatePregnancyMut.mutateAsync({
          id: pregnancyId,
          attrs: { risk_level, risk_flags }
        })
        showToast('Pregnancy risk updated')
        return updated
      } catch (e) {
        getError(e)
        return undefined
      }
    },
    [updatePregnancyMut]
  )

  const endPregnancy = useCallback(
    async (pregnancyId: string, opts: { outcome: EndPregnancyOutcome; endDate?: string; notes?: string }) => {
      const mapped = END_PREGNANCY_OUTCOME_MAP[opts.outcome]
      const episode =
        pregnancies.find(p => p.id === pregnancyId) || (pregnancy?.id === pregnancyId ? pregnancy : undefined)
      const existingNotes = episode?.attributes.notes?.trim() || ''
      const noteParts = [mapped.prefix]
      if (opts.endDate) noteParts.push(`[end_date:${opts.endDate}]`)
      if (opts.notes?.trim()) noteParts.push(opts.notes.trim())
      const mergedNotes = [existingNotes, noteParts.join(' ')].filter(Boolean).join('\n')

      try {
        const updated = await updatePregnancyMut.mutateAsync({
          id: pregnancyId,
          attrs: { status: mapped.status, notes: mergedNotes }
        })

        const openVisits = visits.filter(
          v =>
            v.attributes.pregnancy_id === pregnancyId &&
            (v.attributes.status === 'scheduled' || v.attributes.status === 'in_progress')
        )

        const toCancel =
          openVisits.length > 0
            ? openVisits
            : visits.filter(v => v.attributes.status === 'scheduled' || v.attributes.status === 'in_progress')

        await Promise.all(toCancel.map(v => patchAncVisit(v.id, { status: 'cancelled' })))

        invalidateAnc()
        showToast('Pregnancy episode ended')
        return updated
      } catch (e) {
        getError(e)
        return undefined
      }
    },
    [pregnancies, pregnancy, visits, updatePregnancyMut, invalidateAnc]
  )

  const startLabour = useCallback(
    async (pregnancyId: string) => {
      try {
        const updated = await startLabourApi(pregnancyId)
        invalidateAnc()
        showToast('Labour started')
        return updated
      } catch (e) {
        getError(e)
        return undefined
      }
    },
    [invalidateAnc]
  )

  const recordDelivery = useCallback(
    async (pregnancyId: string, input: RecordDeliveryInput) => {
      try {
        const updated = await recordDeliveryApi(pregnancyId, input)
        invalidateAnc()
        showToast('Delivery recorded — postnatal schedule created')
        return updated
      } catch (e) {
        getError(e)
        return undefined
      }
    },
    [invalidateAnc]
  )

  const completeEpisode = useCallback(
    async (pregnancyId: string) => {
      try {
        const updated = await completePregnancyEpisode(pregnancyId)
        invalidateAnc()
        showToast('Pregnancy episode completed')
        return updated
      } catch (e) {
        getError(e)
        return undefined
      }
    },
    [invalidateAnc]
  )

  const savePostnatalVisit = useCallback(
    async (visitId: string, input: UpsertPostnatalVisitInput) => {
      try {
        const updated = await patchPostnatalVisit(visitId, input)
        invalidateAnc()
        showToast(input.status === 'completed' ? 'Postpartum visit completed' : 'Postpartum visit saved')
        return updated
      } catch (e) {
        getError(e)
        return undefined
      }
    },
    [invalidateAnc]
  )

  const visitCounts = useCallback(
    (visitId: string) => {
      if (visit?.id === visitId) {
        return { order_count: orders.length, rx_count: prescriptions.length }
      }
      return { order_count: 0, rx_count: 0 }
    },
    [visit?.id, orders.length, prescriptions.length]
  )

  return {
    isMock: USE_ANC_MOCK,
    apiResources: ANC_API_RESOURCES,
    pregnancies: pregnancies as PregnancyEpisode[],
    pastPregnancies: pastPregnancies as PregnancyEpisode[],
    pregnancy,
    visits,
    postnatalVisits: postnatalVisits as PostnatalVisit[],
    visit: visit as AncVisit | undefined,
    encounter,
    orders,
    prescriptions,
    invoiceLines,
    invoiceTotal,
    templates,
    formTemplate,
    registerRows,
    labourEpisodes: labourEpisodes as PregnancyEpisode[],
    postpartumRegister: postpartumRegister as PostnatalVisit[],
    gestationalAge,
    nextAncNumber,
    visitCounts,
    bookPregnancy,
    saveVisit,
    startVisit,
    addOrder,
    addPrescription,
    updateTemplate,
    createTemplate,
    updatePregnancyRisk,
    endPregnancy,
    startLabour,
    recordDelivery,
    completeEpisode,
    savePostnatalVisit,
    pnFormTemplate: DEFAULT_PN_TEMPLATE,
    resetPrototype: () => undefined,
    listMockLabWorklist: () => [],
    listMockImagingWorklist: () => []
  }
}
